import React, { useEffect, useRef, useState } from "react";

import { CircularProgress } from "@mui/material";
import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import {
  DNDFieldsNames,
  generateSlotSourceId,
  DNDSourceTypes,
} from "../Viewers/useDragSystem/slotTypes";
import { EmptyContainer, LoadingContainer, ResourceItem, ResourceItemChild } from "./styles";
import { useOperationDespatchContext } from "Modules/moving/contexts/DespatchContext";
import { createDragPreview } from "../Viewers/useDragSystem/DragPreview";

interface ItemType<T> {
  id: number;
  sourceType: DNDSourceTypes;
  payload: T;
  isDisabled?: boolean;
}

type LoadingPosition = "top" | "bottom";

interface ResourceListProps<T> {
  resourceName: DNDFieldsNames;
  /**
   * This property will be used because we have problem to have lists with the same index
   * So, we will merge this indexPrefix with the actual index for the list
   * e.g: indexPrefix = 100000 | index = 3 => 100003
   * The indexPrefix must be a high number like 100000
   */
  indexPrefix: number;
  items: ItemType<T>[];
  render?: (value: ItemType<T>) => React.ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean | { render: boolean; position?: LoadingPosition };
  emptyMessage?: string;
  isDropDisabled?: boolean;
}

function DraggableResourceItem<T>({
  item,
  index,
  indexPrefix,
  resourceName,
  isDisabled,
  render,
}: {
  item: ItemType<T>;
  index: number;
  indexPrefix: number;
  resourceName: DNDFieldsNames;
  isDisabled?: boolean;
  render?: (value: ItemType<T>) => React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { setDraggedItemHeight } = useOperationDespatchContext();

  const sourceId = generateSlotSourceId(item.id, "source", resourceName, item.sourceType);
  const canDrag = !isDisabled && !item?.isDisabled;

  useEffect(() => {
    const element = ref.current;
    if (!element || !canDrag) return;

    return draggable({
      element,
      getInitialData: () => ({
        sourceId: sourceId,
        type: resourceName,
        sourceType: item.sourceType,
        index: indexPrefix + index,
        itemId: item.id,
        item: item,
      }),
      onGenerateDragPreview: createDragPreview(element),
      onDragStart: () => {
        const rect = element.getBoundingClientRect();
        setDraggedItemHeight(rect.height);

        setIsDragging(true);
      },
      onDrop: () => setIsDragging(false),
    });
  }, [item, index, sourceId, canDrag, resourceName, item.sourceType, indexPrefix]);

  return (
    <ResourceItem canDrag={canDrag} isDragging={isDragging} ref={ref}>
      {render?.(item)}
    </ResourceItem>
  );
}

export function ResourceList<T = unknown>(props: ResourceListProps<T>) {
  const {
    resourceName,
    items,
    indexPrefix,
    render,
    isDisabled,
    isLoading,
    emptyMessage,
    isDropDisabled = true,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Set up the list container as a drop target (if not disabled)
  useEffect(() => {
    const element = containerRef.current;
    if (!element || isDropDisabled) return;

    return dropTargetForElements({
      element,
      getData: () => ({
        resourceName: resourceName,
        type: "resource-list",
      }),
      onDragEnter: () => setIsDraggingOver(true),
      onDragLeave: () => setIsDraggingOver(false),
      onDrop: () => setIsDraggingOver(false),
    });
  }, [resourceName, isDropDisabled]);

  const renderLoading = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <CircularProgress size={22} />
        </LoadingContainer>
      );
    }

    return null;
  };

  const renderEmpty = () => {
    if (!emptyMessage) return null;

    return (
      <React.Fragment>
        <EmptyContainer>
          <span>{emptyMessage}</span>
        </EmptyContainer>
      </React.Fragment>
    );
  };

  const isLoadingAtTop = typeof isLoading === "object" && isLoading.position === "top";

  const renderChild = () => {
    return (
      <React.Fragment>
        {!!(isLoading && isLoadingAtTop) && renderLoading()}
        {!!(!isLoading && !items.length) && renderEmpty()}
        {items.map((item, index) => {
          const id = generateSlotSourceId(item.id, "source", resourceName, item.sourceType);

          return (
            <DraggableResourceItem
              key={id}
              item={item}
              index={index}
              indexPrefix={indexPrefix}
              resourceName={resourceName}
              isDisabled={isDisabled}
              render={render}
            />
          );
        })}
        {!!(isLoading && !isLoadingAtTop) && renderLoading()}
      </React.Fragment>
    );
  };

  return (
    <ResourceItemChild isDraggingOver={isDraggingOver} ref={containerRef}>
      {renderChild()}
    </ResourceItemChild>
  );
}
